import { gql } from '@apollo/client';
import { Box, Divider, Grid, Skeleton, Typography } from '@mui/material';
import { CustomDialog } from 'components/common/CustomDialog';
import { CUSTOM_RANGE_LABEL } from 'components/common/DatePicker/DateRangePicker';
import { InfiniteScrollBoundary } from 'components/common/InfiniteScrollBoundary';
import { IconLinearMonitorMobile } from 'components/icons/components/linear/IconLinearMonitorMobile';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import { IconOutlineCalendar1 } from 'components/icons/components/outline/IconOutlineCalendar1';
import { useUserContext } from 'contexts/users/User.context';
import { CreatorAvatar } from 'features/creator';
import {
  NestedFiltersMenuItemBaseValueType,
  NestedFiltersMenuView,
  NestedFiltersOptionType,
} from 'features/nestedFilters';
import { generateDateRangeOptions } from 'features/socialListeningCreatorProfile';
import { platformLabelMap, SocialPostCardView } from 'features/socialPost';
import {
  BrandContentType,
  BrandFragmentBrandCompetitorsShareOfVoiceSocialPostsDialogFragment,
  CompetitorShareOfVoiceData,
  CreatorFragmentCreatorAvatarFragmentDoc,
  PaginatedBrandInboundFiltersInputSortField,
  Platform,
  SocialPostFragmentSocialPostCardViewFragmentDoc,
  SortOrder,
  useGetBrandInboundSocialPostsForBrandCompetitorsShareOfVoiceSocialPostsDialogQuery,
  useGetCompetitorShareOfVoiceAggregatedDataForBrandCompetitorsShareOfVoiceSocialPostsDialogQuery,
} from 'graphql/generated';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import { Filters } from './BrandCompetitorsShareOfVoiceView';

export const BRAND_FRAGMENT_BRAND_COMPETITORS_SHARE_OF_VOICE_SOCIAL_POSTS_DIALOG = gql`
  fragment BrandFragmentBrandCompetitorsShareOfVoiceSocialPostsDialog on BrandModel {
    id
    name
    creators {
      id
      ...CreatorFragmentCreatorAvatar
    }
  }
  ${CreatorFragmentCreatorAvatarFragmentDoc}
`;

// eslint-disable-next-line
gql`
  query GetCompetitorShareOfVoiceAggregatedDataForBrandCompetitorsShareOfVoiceSocialPostsDialog(
    $filters: CompetitorShareOfVoiceFiltersInput!
  ) {
    competitorShareOfVoiceAggregatedData(filters: $filters) {
      data {
        id
        aggregatedData {
          shareOfVoice
          mentionCount
          viewCount
          totalEngagement
          engagementRate
        }
      }
    }
  }
`;

// eslint-disable-next-line
gql`
  query GetBrandInboundSocialPostsForBrandCompetitorsShareOfVoiceSocialPostsDialog(
    $filters: PaginatedBrandInboundFiltersInputForSocialPosts!
  ) {
    getBrandInboundSocialPosts(data: $filters) {
      data {
        ...SocialPostFragmentSocialPostCardView
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${SocialPostFragmentSocialPostCardViewFragmentDoc}
`;

export type BrandCompetitorsShareOfVoiceSocialPostsDialogProps = {
  brandId: string;
  competitorTrackingBrand: BrandFragmentBrandCompetitorsShareOfVoiceSocialPostsDialogFragment;
  initialAggregatedData?: CompetitorShareOfVoiceData; // We should benefit from the aggregated data already being loaded in the parent component
  isOpen: boolean;
  filters: Pick<Filters, 'platforms' | 'dateRange'>;
  onClose: () => void;
};
export const BrandCompetitorsShareOfVoiceSocialPostsDialog = (
  props: BrandCompetitorsShareOfVoiceSocialPostsDialogProps,
) => {
  const {
    brandId,
    competitorTrackingBrand,
    initialAggregatedData,
    isOpen,
    filters,
    onClose,
  } = props;

  const { user } = useUserContext();
  const brand = useMemo(
    () => user?.socialListeningBrands.find((b) => b.id === brandId),
    [user, brandId],
  );

  const [internalFilters, setInternalFilters] = useState(filters);

  const dateRangeOptions = useMemo(() => generateDateRangeOptions(), []);
  const selectedDateRangeOption = useMemo(
    () =>
      dateRangeOptions.find(
        (o) => o.label === internalFilters.dateRange.value?.label,
      ),
    [dateRangeOptions, internalFilters.dateRange],
  );
  const selectedDateRangeLabel = useMemo(() => {
    if (internalFilters.dateRange.value?.label === CUSTOM_RANGE_LABEL) {
      return CUSTOM_RANGE_LABEL;
    }
    return selectedDateRangeOption?.label;
  }, [internalFilters.dateRange.value?.label, selectedDateRangeOption]);

  // Manage stats

  const { data: statsData, loading: statsLoading } =
    useGetCompetitorShareOfVoiceAggregatedDataForBrandCompetitorsShareOfVoiceSocialPostsDialogQuery(
      {
        variables: {
          filters: {
            brandId,
            brandIds: [competitorTrackingBrand.id],
            platforms: internalFilters.platforms.value?.map((p) => p.value),
            dateRange: {
              startDate: internalFilters.dateRange.value?.startDate,
              endDate: internalFilters.dateRange.value?.endDate,
            },
          },
        },
        fetchPolicy: 'cache-and-network',
      },
    );
  const isLoadingStats = statsLoading || !statsData;
  const aggregatedData = useMemo(() => {
    // If the stats are still loading, we can use the initial aggregated data
    // for a quick fallback
    if (!isLoadingStats && initialAggregatedData) {
      return initialAggregatedData;
    }

    if (statsData?.competitorShareOfVoiceAggregatedData.data.length) {
      return statsData.competitorShareOfVoiceAggregatedData.data[0]
        .aggregatedData;
    }

    return {
      shareOfVoice: 0,
      mentionCount: 0,
      viewCount: 0,
      totalEngagement: 0,
      engagementRate: 0,
    };
  }, [statsData, isLoadingStats, initialAggregatedData]);

  const stats = useMemo(() => {
    const renderSkeleton = (width = 50) => {
      return <Skeleton variant="text" width={width} height={40} />;
    };

    return [
      {
        label: 'Share of Voice',
        text: isLoadingStats
          ? renderSkeleton()
          : `${((aggregatedData?.shareOfVoice || 0) * 100).toFixed(2)}%`,
      },
      {
        label: 'Total Post Count',
        text: isLoadingStats
          ? renderSkeleton()
          : formatBigNumber(aggregatedData?.mentionCount ?? 0),
      },
      {
        label: 'Total View Count',
        text: isLoadingStats
          ? renderSkeleton()
          : formatBigNumber(aggregatedData?.viewCount ?? 0),
      },
      {
        label: 'Total Engagement',
        text: isLoadingStats
          ? renderSkeleton()
          : formatBigNumber(aggregatedData?.totalEngagement ?? 0),
      },
      {
        label: 'Average Engagement Rate',
        text: isLoadingStats
          ? renderSkeleton(90)
          : `${((aggregatedData?.engagementRate || 0) * 100).toFixed(2)}%`,
      },
    ];
  }, [aggregatedData, internalFilters, isLoadingStats]); // eslint-disable-line react-hooks/exhaustive-deps

  const creatorWithProfilePicture = useMemo(() => {
    return competitorTrackingBrand.creators.find((c) => c.profilePictureUrl);
  }, [competitorTrackingBrand.creators]);

  // Manage social posts

  const [socialPostsSortKey, setSocialPostsSortKey] = useState(
    PaginatedBrandInboundFiltersInputSortField.TotalImpressions,
  );
  const sortOptions = [
    {
      label: 'Total View Count',
      value: PaginatedBrandInboundFiltersInputSortField.TotalImpressions,
    },
    {
      label: 'Total Engagement',
      value: PaginatedBrandInboundFiltersInputSortField.TotalEngagement,
    },
  ];

  const socialPostFilters = useMemo(() => {
    return {
      brandId: competitorTrackingBrand.id,
      platforms: internalFilters.platforms.value?.map((p) => p.value),
      dateRange: {
        startDate: internalFilters.dateRange.value?.startDate,
        endDate: internalFilters.dateRange.value?.endDate,
      },
      sortBy: {
        field: socialPostsSortKey,
        order: SortOrder.Desc,
      },
      contentType: BrandContentType.Ugc,
    };
  }, [internalFilters, socialPostsSortKey, competitorTrackingBrand.id]);

  const {
    data: socialPostsData,
    loading: isLoadingInitialSocialPosts,
    fetchMore: fetchMoreSocialPosts,
  } = useGetBrandInboundSocialPostsForBrandCompetitorsShareOfVoiceSocialPostsDialogQuery(
    {
      variables: {
        filters: {
          ...socialPostFilters,
        },
      },
    },
  );
  const socialPosts = socialPostsData?.getBrandInboundSocialPosts.data || [];
  const [isLoadingMoreSocialPosts, setIsLoadingMoreSocialPosts] =
    useState(false);
  const isLoadingSocialPosts =
    isLoadingInitialSocialPosts || isLoadingMoreSocialPosts;

  const onFetchMoreSocialPosts = useCallback(async () => {
    setIsLoadingMoreSocialPosts(true);
    await fetchMoreSocialPosts({
      variables: {
        filters: {
          ...socialPostFilters,
          after: socialPostsData?.getBrandInboundSocialPosts.pageInfo.endCursor,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          getBrandInboundSocialPosts: {
            ...fetchMoreResult.getBrandInboundSocialPosts,
            data: [
              ...prev.getBrandInboundSocialPosts.data,
              ...fetchMoreResult.getBrandInboundSocialPosts.data,
            ],
          },
        };
      },
    });
    setIsLoadingMoreSocialPosts(false);
  }, [socialPostFilters, socialPostsData, fetchMoreSocialPosts]);

  if (!brand) {
    return null;
  }

  return (
    <CustomDialog
      open={isOpen}
      onClose={onClose}
      onBackClick={onClose}
      title="Social Posts"
      titleBreadcrumbs={[
        '🔔 Social Listening',
        brand.name,
        'Competitor Analytics',
        competitorTrackingBrand.name,
      ]}
    >
      <Box
        sx={{
          p: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {creatorWithProfilePicture && (
              <CreatorAvatar
                creator={creatorWithProfilePicture}
                size={48}
                hidePlatformIcon
              />
            )}
            <Typography variant="headline-xl" fontSize={24} fontWeight={600}>
              {competitorTrackingBrand.name}
            </Typography>
          </Box>
          <Box display="flex" gap={3} alignItems="center">
            <NestedFiltersMenuView
              items={[
                {
                  type: 'multi-select',
                  key: 'platforms',
                  options: [
                    Platform.Instagram,
                    Platform.Tiktok,
                    Platform.Youtube,
                  ].map((platform) => ({
                    label: platformLabelMap[platform],
                    value: platform,
                  })),
                },
              ]}
              values={{
                platforms: internalFilters.platforms,
              }}
              onChange={(values) => {
                setInternalFilters({
                  ...internalFilters,
                  platforms:
                    values.platforms as NestedFiltersMenuItemBaseValueType<
                      {
                        label: string;
                        value: Platform;
                      }[]
                    >,
                });
              }}
              componentsProps={{
                trigger: {
                  text: internalFilters.platforms.value?.length
                    ? internalFilters.platforms.value
                        .map((p) => p.label)
                        .join(', ')
                    : 'Platforms',
                  Icon: IconLinearMonitorMobile,
                  sx: {
                    bgcolor: theme.colors?.primary.parchment,
                  },
                },
              }}
            />
            <NestedFiltersMenuView
              items={[
                {
                  type: 'date-range',
                  key: 'dateRange',
                  options: dateRangeOptions,
                },
              ]}
              values={{
                dateRange: internalFilters.dateRange,
              }}
              onChange={(values) => {
                setInternalFilters({
                  ...internalFilters,
                  dateRange:
                    values.dateRange as NestedFiltersMenuItemBaseValueType<{
                      label: string;
                      startDate: Date;
                      endDate: Date;
                    }>,
                });
              }}
              componentsProps={{
                trigger: {
                  text: selectedDateRangeLabel || 'Date Range',
                  Icon: IconOutlineCalendar1,
                  sx: {
                    bgcolor: theme.colors?.primary.parchment,
                  },
                },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            background: theme.colors?.primary.parchment,
            borderRadius: 3,
            display: 'flex',
            flexWrap: 'wrap',
            p: 4,
            gap: 4,
          }}
        >
          {stats.map((stat, index) => (
            <Box
              key={stat.label}
              sx={{
                [theme.breakpoints.up('md')]: {
                  borderRight:
                    index + 1 !== stats.length
                      ? `1px solid rgba(35, 6, 3, 0.07)`
                      : 'none',
                },
                flex: 1,
                minWidth: 140,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                }}
              >
                <Typography variant="headline-sm">{stat.label}</Typography>
                <Typography
                  variant="headline-xl"
                  fontSize={32}
                  fontWeight={500}
                >
                  {stat.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Divider
          sx={{
            bgcolor: 'rgba(35, 6, 3, 0.05)',
            height: 2,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="headline-lg"
              fontSize={20}
              color={theme.colors?.utility[900]}
            >
              Posts
            </Typography>
            <NestedFiltersMenuView
              values={{
                sort: {
                  value: sortOptions.find(
                    (option) => option.value === socialPostsSortKey,
                  ),
                },
              }}
              items={[
                {
                  type: 'single-select',
                  key: 'sort',
                  label: 'Sort by',
                  options: sortOptions,
                },
              ]}
              onChange={(values) => {
                setSocialPostsSortKey(
                  (values.sort.value as NestedFiltersOptionType)
                    ?.value as PaginatedBrandInboundFiltersInputSortField,
                );
              }}
              componentsProps={{
                trigger: {
                  text:
                    sortOptions.find(
                      (option) => option.value === socialPostsSortKey,
                    )?.label ?? 'Sort',
                  Icon: IconLinearSort,
                  sx: {
                    bgcolor: theme.colors?.primary.parchment,
                  },
                },
              }}
            />
          </Box>
          {socialPosts.length ? (
            <>
              <Grid container spacing={6}>
                {socialPosts.map((socialPost) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      lg={2.4}
                      key={socialPost.id}
                      component={Link}
                      to={PlotRoutes.socialListeningPost(
                        socialPost.id,
                        brandId,
                      )}
                    >
                      <SocialPostCardView socialPost={socialPost} />
                    </Grid>
                  );
                })}
              </Grid>
              <InfiniteScrollBoundary
                disabled={
                  isLoadingSocialPosts ||
                  !socialPostsData?.getBrandInboundSocialPosts.pageInfo
                    .hasNextPage
                }
                onVisible={onFetchMoreSocialPosts}
              />
            </>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography
                variant="headline-sm"
                color={theme.colors?.utility[600]}
              >
                No social posts spotted 👀
              </Typography>
            </Box>
          )}
          {isLoadingSocialPosts && (
            <Grid container spacing={6}>
              {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={3} lg={2.4} key={index}>
                  <Skeleton variant="rectangular" height={200} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </CustomDialog>
  );
};
